import { useEffect, useMemo, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Configuration, Flux, searchFlux } from '@/api'

export const WORKFLOW_OPTIONS_PAGE_SIZE = 50

const SEARCH_DEBOUNCE_MS = 300

/**
 * Les modèles de l'agence, page par page et filtrés par nom côté serveur. `decIntegrationId` part
 * au serveur, mais `searchFlux` ne filtre pas le type de déclaration : il est refait côté client.
 * La fin de liste se lit donc sur la page brute, et tant que le filtre laisse moins d'une page,
 * la suivante est chargée sans attendre le défilement (une liste trop courte ne défile pas).
 */
export const useWizardWorkflowOptions = ({
  groupId,
  configuration,
}: {
  groupId?: string
  configuration?: Configuration
}) => {
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setSearch('')
    setQuery('')
  }, [groupId])

  const decIntegrationId = configuration?.decIntegrationId || undefined
  const declarationType = configuration?.decTypeName

  const result = useInfiniteQuery(
    ['wizardWorkflow', groupId, decIntegrationId, query],
    async ({ pageParam = 0 }: { pageParam?: number }) => {
      const response = await searchFlux({
        name: query || undefined,
        groupId,
        decIntegrationId,
        page: pageParam,
        size: WORKFLOW_OPTIONS_PAGE_SIZE,
      })
      const raw = response?.data
      return { page: pageParam, raw: Array.isArray(raw) ? raw : [] }
    },
    {
      enabled: Boolean(groupId),
      getNextPageParam: (last) =>
        last.raw.length >= WORKFLOW_OPTIONS_PAGE_SIZE
          ? last.page + 1
          : undefined,
    }
  )

  const options = useMemo<Flux[]>(() => {
    const all = (result.data?.pages ?? []).flatMap((page) => page.raw)
    if (!configuration) return all
    return all.filter(
      (workflow) =>
        workflow.declarationType === declarationType &&
        (!decIntegrationId || workflow.decIntegrationId === decIntegrationId)
    )
  }, [result.data, configuration, declarationType, decIntegrationId])

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = result

  useEffect(() => {
    if (
      options.length < WORKFLOW_OPTIONS_PAGE_SIZE &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      void fetchNextPage()
    }
  }, [options.length, hasNextPage, isFetchingNextPage, fetchNextPage])

  return {
    search,
    setSearch,
    options,
    isLoading: result.isLoading && result.fetchStatus !== 'idle',
    isFetchingNextPage,
    hasNextPage: Boolean(hasNextPage),
    fetchNextPage,
  }
}
